import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import type { ContentSummary, ContentTypeDefinition } from '@cms/contracts'
import { useI18n } from '../i18n/context.js'
import { ApiError } from '../api/client.js'
import { getSite, listContents, listContentTypes } from '../api/endpoints.js'
import { useCan } from '../auth/session.js'
import { useSiteWorkspace } from '../workspace/site.js'
import { absoluteDateTime, relativeTime } from '../domain/format.js'
import { liveUrl } from '../domain/live-url.js'
import {
  buildEntries, buildTree, countByType, filterEntries, orderedLocales, publicPath, sectionKeys, titleOf,
  type TreeContext, type TreeEntry, type TreeNode, type TypeInfo,
} from '../domain/content-tree.js'
import { Thumb } from '../components/Thumb.js'
import { editEntry, useInPlaceSettings } from '../components/EditOnSite.js'
import { editOnSite } from '../domain/in-place-edit.js'
import { paths, type ContentListFilters } from '../routes.js'
import { TextField } from '../ui/fields.js'
import { MenuButton, type MenuEntry } from '../ui/overlays.js'
import { Banner, EmptyState, FilterChip, FilterChipLink, Icon, LinkButton, PageHeader, StatusChip, TableSkeleton, cx } from '../ui/primitives.js'
import { contentStatusTone, contentTypeIcon } from '../ui/status.js'
import './content-tree.css'

const STATUSES: ContentSummary['status'][] = ['draft', 'review', 'published', 'trashed']
const PAGE = 100
/** More than this and the tree would be slower than useful; the rest is reached by searching. */
const MAX_ITEMS = 2000
/** A site this small is opened in full; a larger one starts closed so its shape is readable. */
const OPEN_ALL_UNDER = 60

async function loadAllContents(siteId: string, onProgress: (done: number, total: number) => void, alive: () => boolean): Promise<{ items: ContentSummary[]; total: number }> {
  const first = await listContents({ siteId, limit: PAGE })
  const total = first.total
  const items = [...first.items]
  onProgress(items.length, total)
  const wanted = Math.min(total, MAX_ITEMS)
  const offsets: number[] = []
  for (let offset = PAGE; offset < wanted; offset += PAGE) offsets.push(offset)
  for (let start = 0; start < offsets.length && alive(); start += 4) {
    const batch = await Promise.all(offsets.slice(start, start + 4).map((offset) => listContents({ siteId, limit: PAGE, cursor: String(offset) })))
    for (const page of batch) items.push(...page.items)
    onProgress(items.length, total)
  }
  return { items, total }
}

function languageName(code: string, uiLocale: string, capital = false): string {
  try {
    const name = new Intl.DisplayNames([uiLocale], { type: 'language' }).of(code) ?? code.toUpperCase()
    return capital ? name.charAt(0).toUpperCase() + name.slice(1) : name
  } catch {
    return code.toUpperCase()
  }
}

/**
 * The contents of a site as a tree by public address, in one language.
 *
 * A migrated site has hundreds of contents in two languages; a flat table
 * hides its shape. Here single pages come first, then sections («Blog»,
 * «Corsi») that open and close, with the translations of each item as small
 * labels and a clear mark where one is missing. The structure is in
 * `domain/content-tree.ts`; filters that people share (type, status, words)
 * stay in the URL.
 */
export function ContentListScreen() {
  const { t, locale } = useI18n()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { siteId, site } = useSiteWorkspace()
  const canWrite = useCan('cms:content:write')

  const type = searchParams.get('type') ?? ''
  const status = searchParams.get('status') ?? ''
  const q = searchParams.get('q') ?? ''
  const filters: ContentListFilters = { type: type || undefined, status: status || undefined, q: q || undefined }

  const [contentTypes, setContentTypes] = useState<ContentTypeDefinition[]>([])
  const [typesError, setTypesError] = useState<string | null>(null)
  const [items, setItems] = useState<ContentSummary[] | null>(null)
  const [total, setTotal] = useState(0)
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState(q)
  const [siteLocales, setSiteLocales] = useState<{ locales: string[]; main: string | null }>({ locales: [], main: null })
  const [lang, setLang] = useState<string | null>(null)
  const [open, setOpen] = useState<Set<string>>(new Set())
  const now = Date.now()

  useEffect(() => setSearch(q), [q])

  useEffect(() => {
    listContentTypes()
      .then((types) => { setContentTypes(types); setTypesError(null) })
      .catch((err: unknown) => setTypesError(err instanceof ApiError ? err.message : t('errors.network')))
  }, [t])

  useEffect(() => {
    let alive = true
    setSiteLocales({ locales: [], main: null })
    setLang(null)
    getSite(siteId)
      .then((detail) => { if (alive) setSiteLocales({ locales: detail.locales ?? [], main: detail.defaultLocale ?? null }) })
      .catch(() => undefined)
    return () => { alive = false }
  }, [siteId])

  // The URL is replaced, not pushed, so Back leaves the list; the words filter the tree at once.
  useEffect(() => {
    const words = search.trim()
    if (words === q) return
    const timer = window.setTimeout(() => navigate(paths.contents(siteId, { ...filters, q: words || undefined }), { replace: true }), 400)
    return () => window.clearTimeout(timer)
  }, [search])

  useEffect(() => {
    let alive = true
    setItems(null)
    setError(null)
    setProgress(null)
    loadAllContents(siteId, (done, all) => { if (alive) setProgress({ done, total: all }) }, () => alive)
      .then((result) => { if (alive) { setItems(result.items); setTotal(result.total) } })
      .catch((err: unknown) => { if (alive) setError(err instanceof ApiError ? err.message : t('errors.network')) })
    return () => { alive = false }
  }, [siteId, t])

  const typesByKey = useMemo(() => new Map(contentTypes.map((ct) => [ct.key, ct])), [contentTypes])
  const typeLabel = useCallback((key: string) => {
    const label = typesByKey.get(key)?.label
    return label?.[locale] ?? label?.it ?? key
  }, [typesByKey, locale])
  const typeInfo = useMemo(() => new Map<string, TypeInfo>(contentTypes.map((ct) => [ct.key, { label: typeLabel(ct.key), urlPattern: ct.urlPattern }])), [contentTypes, typeLabel])

  const locales = useMemo(() => orderedLocales(siteLocales.locales, siteLocales.main, items ?? []), [siteLocales, items])
  const shownLocale = lang && locales.includes(lang) ? lang : locales[0] ?? 'it'
  const context: TreeContext = useMemo(() => ({ locale: shownLocale, locales, types: typeInfo }), [shownLocale, locales, typeInfo])

  const entries = useMemo(() => buildEntries(items ?? [], context), [items, context])
  const inLanguage = useMemo(() => entries.filter((entry) => entry.inLocale), [entries])
  const typeCounts = useMemo(() => countByType(inLanguage), [inLanguage])
  const words = search.trim()
  const visible = useMemo(() => filterEntries(entries, { type, status, words }, typeLabel), [entries, type, status, words, typeLabel])
  const tree = useMemo(
    () => buildTree(visible, context, { typeLabel, otherLabel: t('contentTree.otherLanguages') }),
    [visible, context, typeLabel, t],
  )
  const searching = words.length > 0 || !!type || !!status

  // A small site opens in full; a large one starts closed. Changing language starts over.
  const seeded = useRef('')
  useEffect(() => {
    if (!items) return
    const stamp = `${siteId}|${shownLocale}|${items.length}`
    if (seeded.current === stamp) return
    seeded.current = stamp
    setOpen(inLanguage.length <= OPEN_ALL_UNDER ? new Set(sectionKeys(tree)) : new Set())
  }, [items, siteId, shownLocale, inLanguage.length, tree])

  const toggle = (key: string) => setOpen((current) => {
    const next = new Set(current)
    if (!next.delete(key)) next.add(key)
    return next
  })
  const href = (change: Partial<ContentListFilters>) => paths.contents(siteId, { ...filters, ...change })
  const filtered = !!(type || status || q)
  const loading = items === null && !error

  const inPlace = useInPlaceSettings(siteId)
  const rowMenu = (item: ContentSummary): MenuEntry[] => {
    const itemPath = publicPath(item, { types: typeInfo })
    const url = liveUrl(site?.primaryDomain, itemPath)
    const edit = editOnSite({ settings: inPlace, path: itemPath, online: item.status === 'published', primaryDomain: site?.primaryDomain })
    return [
      { key: 'open', label: t('list.actions.open'), icon: 'edit', to: paths.content(siteId, item.id) },
      { key: 'page', label: t('page.openInPage'), icon: 'web', to: paths.contentPage(siteId, item.id) },
      { key: 'history', label: t('list.openHistory'), icon: 'history', to: paths.contentHistory(siteId, item.id) },
      ...(url ? [{ key: 'live', label: t('live.openOnSite'), icon: 'public' as const, href: url, divider: true, disabled: item.status !== 'published', secondary: item.status !== 'published' ? t('live.notPublishedYet') : undefined }] : []),
      ...(canWrite && edit.kind !== 'loading' ? [editEntry(t, edit)] : []),
    ]
  }

  const languageChips = (entry: TreeEntry) => {
    const byLocale = new Map([entry.main, ...entry.translations].map((member) => [member.locale, member]))
    return <span className="ctree-langs">
      {locales.map((code) => {
        const member = byLocale.get(code)
        const name = languageName(code, locale)
        if (!member) {
          return <span key={code} className="lang-chip is-missing" title={t('contentTree.missingHelp', { language: name })}>
            <Icon name="link_off" size={18}/>{t('contentTree.missing', { lang: code.toUpperCase() })}
          </span>
        }
        if (member.id === entry.main.id && entry.inLocale) return <span key={code} className={cx('lang-chip', 'is-current', `tone-${member.status}`)}>{code.toUpperCase()}</span>
        return <Link key={code} to={paths.content(siteId, member.id)} className={cx('lang-chip', 'is-link', `tone-${member.status}`)}
          title={t('contentTree.translationHelp', { language: name, title: titleOf(member), status: t(`status.${member.status}`) })}>
          {code.toUpperCase()}
        </Link>
      })}
    </span>
  }

  const renderNodes = (nodes: readonly TreeNode[], depth: number) => <ul className="ctree-list">
    {nodes.map((node) => {
      const entry = node.entry
      const style = { '--depth': depth } as CSSProperties
      if (!entry) {
        const isOpen = searching || open.has(node.key)
        return <li key={node.key} className="ctree-item">
          <button type="button" className={cx('ctree-row', 'ctree-section', node.virtual && 'is-virtual')} style={style} aria-expanded={isOpen}
            aria-label={isOpen ? t('contentTree.closeSection', { name: node.label }) : t('contentTree.openSection', { name: node.label })}
            title={node.key === '~other' ? t('contentTree.otherLanguagesHelp') : undefined}
            onClick={() => { if (!searching) toggle(node.key) }}>
            <Icon name={isOpen ? 'expand_more' : 'chevron_right'} size={24}/>
            <span className="thumb thumb-sm thumb-empty"><Icon name={node.key === '~other' ? 'translate' : 'view_list'} size={24}/></span>
            <span className="row-title"><strong>{node.label}</strong><small>{node.count === 1 ? t('contentTree.countOne') : t('contentTree.countMany', { n: node.count })}</small></span>
          </button>
          {isOpen && renderNodes(node.children, depth + 1)}
        </li>
      }
      const item = entry.main
      return <li key={node.key} className="ctree-item">
        <div className="ctree-row ctree-leaf" style={style}>
          <Link to={paths.content(siteId, item.id)} className="row-link ctree-main">
            <Thumb image={item.mainImage} icon={contentTypeIcon(item.contentTypeKey)}/>
            <span className="row-title">
              <strong>{item.title?.trim() ? item.title : <em>{t('contentTree.noTitle')}</em>}</strong>
              <small>{typeLabel(item.contentTypeKey)} · {entry.path ?? `/${item.slug}`}</small>
            </span>
          </Link>
          {languageChips(entry)}
          <span className="ctree-status"><StatusChip tone={contentStatusTone(item.status)}>{t(`status.${item.status}`)}</StatusChip></span>
          <time className="ctree-time" dateTime={new Date(item.updatedAt).toISOString()} title={absoluteDateTime(item.updatedAt, locale)}>{relativeTime(item.updatedAt, now, locale)}</time>
          <span className="ctree-actions"><MenuButton label={t('list.actions.for', { title: titleOf(item) })} items={rowMenu(item)}/></span>
        </div>
      </li>
    })}
  </ul>

  const shownName = languageName(shownLocale, locale)

  return <div className="page">
    <PageHeader
      title={t('list.title')}
      subtitle={t('list.subtitleFor', { site: site?.name ?? '' })}
      actions={canWrite ? <LinkButton variant="filled" icon="add" to={paths.newContent(siteId)}>{t('list.newContent')}</LinkButton> : undefined}
    />

    <div className="toolbar">
      <TextField
        className="toolbar-search"
        label={t('list.search.label')}
        hideLabel
        type="search"
        leadingIcon="search"
        value={search}
        placeholder={t('list.search.placeholder')}
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter') navigate(href({ q: search.trim() || undefined }), { replace: true }) }}
      />
      {locales.length > 1 && <div className="chip-row" role="group" aria-label={t('contentTree.language')} title={t('contentTree.languageHelp')}>
        {locales.map((code) => <FilterChip key={code} selected={code === shownLocale} icon="language" onClick={() => setLang(code)}>{languageName(code, locale, true)}</FilterChip>)}
      </div>}
      <div className="chip-row" role="group" aria-label={t('list.filters.status')}>
        <FilterChipLink selected={!status} to={href({ status: undefined })} replace>{t('list.filters.allStatuses')}</FilterChipLink>
        {STATUSES.map((entry) => <FilterChipLink key={entry} selected={status === entry} to={href({ status: status === entry ? undefined : entry })} replace>{t(`status.${entry}`)}</FilterChipLink>)}
      </div>
      {contentTypes.length > 1 && <div className="chip-row" role="group" aria-label={t('list.filters.type')}>
        <FilterChipLink selected={!type} to={href({ type: undefined })} replace>{t('list.filters.allTypes')}</FilterChipLink>
        {contentTypes.map((ct) => <FilterChipLink key={ct.key} selected={type === ct.key} icon={contentTypeIcon(ct.key)} to={href({ type: type === ct.key ? undefined : ct.key })} replace>
          {typeLabel(ct.key)}{typeCounts.has(ct.key) ? <span className="chip-count"> {typeCounts.get(ct.key)}</span> : null}
        </FilterChipLink>)}
      </div>}
    </div>

    {typesError && <Banner tone="warning" title={t('list.filtersErrorTitle')}>{typesError}</Banner>}
    {error && <Banner tone="error" title={t('list.errorTitle')}>{error}</Banner>}
    {items && total > items.length && <Banner tone="info">{t('contentTree.truncated', { n: items.length })}</Banner>}

    <div className="card table-card ctree-card">
      {loading ? <>
        <TableSkeleton rows={8} columns={4} label={t('list.loading')}/>
        {progress && progress.total > progress.done && <p className="ctree-progress" role="status">{t('contentTree.loadingProgress', { done: progress.done, total: progress.total })}</p>}
      </>
        : items && items.length === 0 ? <EmptyState icon="article" title={t('list.emptyTitle')}
          action={canWrite ? <LinkButton variant="filled" icon="add" to={paths.newContent(siteId)}>{t('list.newContent')}</LinkButton> : undefined}
        >{t('list.emptyHelp')}</EmptyState>
        : items && tree.length === 0 ? <EmptyState icon="search" title={filtered ? t('list.emptyFiltered') : t('contentTree.emptyTree')}
          action={filtered ? <LinkButton variant="outlined" to={paths.contents(siteId)}>{t('list.clearFilters')}</LinkButton> : undefined}
        >{filtered ? t('list.emptyFilteredHelp') : t('contentTree.emptyTreeHelp')}</EmptyState>
        : items && <>
          <div className="ctree-bar">
            <span className="ctree-summary" role="status">
              {searching
                ? t('contentTree.summary', { shown: visible.filter((entry) => entry.inLocale).length, total: inLanguage.length, language: shownName })
                : t('contentTree.summaryAll', { total: inLanguage.length, language: shownName })}
            </span>
            {!searching && <span className="ctree-bar-actions">
              <button type="button" className="ctree-link" onClick={() => setOpen(new Set(sectionKeys(tree)))}><Icon name="unfold_more" size={18}/>{t('contentTree.expandAll')}</button>
              <button type="button" className="ctree-link" onClick={() => setOpen(new Set())}><Icon name="unfold_less" size={18}/>{t('contentTree.collapseAll')}</button>
            </span>}
          </div>
          <nav className="ctree" aria-label={t('contentTree.treeLabel')}>{renderNodes(tree, 0)}</nav>
        </>}
    </div>
  </div>
}
